import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, Pencil, Lock, Unlock, Save, X, ShieldAlert,
  ClipboardPaste, CheckCircle2, AlertTriangle,
} from "lucide-react";
import OscarShortcuts from "../components/OscarShortcuts";
import { GoogleAccess } from "../components/GoogleAccess";

const API = process.env.REACT_APP_BACKEND_URL;

const MONTH_ALIASES = {
  ene: 1, en: 1, enero: 1, jan: 1, january: 1,
  feb: 2, febrero: 2, february: 2,
  mar: 3, marzo: 3, march: 3,
  abr: 4, ab: 4, abril: 4, apr: 4, april: 4,
  may: 5, mayo: 5,
  jun: 6, junio: 6, june: 6,
  jul: 7, julio: 7, july: 7,
  ago: 8, ag: 8, agosto: 8, aug: 8, august: 8,
  sep: 9, sept: 9, septiembre: 9, september: 9,
  oct: 10, octubre: 10, october: 10,
  nov: 11, noviembre: 11, november: 11,
  dic: 12, diciembre: 12, dec: 12, december: 12,
};

// Parse a numeric string handling both "1,234.56" (EN) and "1.234,56" (ES) formats.
// Heuristic: the LAST separator followed by 1-2 digits is the decimal separator.
const parseLocalizedNumber = (t) => {
  if (!t) return NaN;
  const lastDot = t.lastIndexOf(".");
  const lastComma = t.lastIndexOf(",");
  if (lastDot === -1 && lastComma === -1) return parseFloat(t);
  let decIdx = -1;
  let decChar = null;
  if (lastDot > lastComma) { decIdx = lastDot; decChar = "."; }
  else if (lastComma > lastDot) { decIdx = lastComma; decChar = ","; }
  if (decIdx >= 0) {
    const tail = t.substring(decIdx + 1);
    if (/^\d{1,2}$/.test(tail)) {
      const intPart = t.substring(0, decIdx).replace(/[.,]/g, "");
      return parseFloat(intPart + "." + tail);
    }
  }
  // Otherwise treat all separators as thousands
  return parseFloat(t.replace(/[.,]/g, ""));
};

// Parses raw Oscar table text. Each "row" is identified by a 4-digit year + month name.
// Numbers afterwards (in order) are: cantidad (noches), totales, estancias [, cancelaciones]
const parseOscarPaste = (text, defaultYear) => {
  if (!text) return [];
  // Normalize: replace €, NBSP, thousands dots/commas
  const clean = text
    .replace(/€/g, " ")
    .replace(/\u00A0/g, " ")
    .replace(/[?¿]/g, " ");

  // Split into tokens by whitespace/newlines, keeping numbers intact
  const tokens = clean.split(/\s+/).filter(Boolean);

  const rows = [];
  let i = 0;
  while (i < tokens.length) {
    const tok = tokens[i].toLowerCase();
    let year = null;
    let month = null;
    // Case A: token is a 4-digit year and next token is a month name
    if (/^\d{4}$/.test(tok)) {
      const nextRaw = (tokens[i + 1] || "").toLowerCase().replace(/[^a-z]/g, "");
      if (MONTH_ALIASES[nextRaw]) {
        year = parseInt(tok, 10);
        month = MONTH_ALIASES[nextRaw];
        i += 2;
      }
    }
    // Case B: token is a month name (use defaultYear)
    if (year === null) {
      const monthOnly = tok.replace(/[^a-z]/g, "");
      if (MONTH_ALIASES[monthOnly] && defaultYear) {
        year = defaultYear;
        month = MONTH_ALIASES[monthOnly];
        i += 1;
      }
    }
    if (year === null || month === null) {
      i += 1;
      continue;
    }
    // Collect next 3-4 numeric tokens (cantidad, totales, estancias, [cancelaciones])
    const nums = [];
    while (i < tokens.length && nums.length < 4) {
      const t = tokens[i];
      // Stop if next token is another month/year header
      if (/^\d{4}$/.test(t)) {
        const peek = (tokens[i + 1] || "").toLowerCase().replace(/[^a-z]/g, "");
        if (MONTH_ALIASES[peek]) break;
      }
      const peekMonth = t.toLowerCase().replace(/[^a-z]/g, "");
      if (MONTH_ALIASES[peekMonth] && nums.length > 0) break;
      // Parse number: handles "1,274.69" (EN) and "1.274,69" (ES)
      const n = parseLocalizedNumber(t);
      if (!isNaN(n)) {
        nums.push(n);
      }
      i += 1;
    }
    if (nums.length >= 3) {
      rows.push({
        year,
        month,
        room_nights: Math.round(nums[0]),
        total_revenue: nums[1],
        revenue: nums[2], // Estancias
      });
    }
  }
  // Dedupe by year+month, keep last
  const map = new Map();
  rows.forEach((r) => map.set(`${r.year}-${r.month}`, r));
  return Array.from(map.values()).sort((a, b) => a.year - b.year || a.month - b.month);
};

const MONTH_NAMES_SHORT = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

const Admin = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [dataType, setDataType] = useState("production");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  // Bulk paste state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkApplying, setBulkApplying] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/admin/monthly-data?year=${year}&data_type=${dataType}`,
        { headers },
      );
      setRecords(res.data.records || []);
    } catch (e) {
      toast.error("Error cargando datos");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [year, dataType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openEdit = (record) => {
    setEditing({
      month: record.month,
      month_name: record.month_name,
      revenue: record.revenue || 0,
      total_revenue: record.total_revenue || 0,
      room_nights: record.room_nights || 0,
      locked: record.locked || false,
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        data_type: dataType,
        revenue: editing.revenue,
        locked: editing.locked,
      };
      if (dataType === "production") {
        payload.room_nights = editing.room_nights;
        payload.total_revenue = editing.total_revenue;
      }
      await axios.patch(
        `${API}/api/admin/monthly-data/${year}/${editing.month}`,
        payload,
        { headers },
      );
      toast.success(`${editing.month_name} ${year} actualizado`);
      setEditing(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const toggleLock = async (record) => {
    try {
      await axios.patch(
        `${API}/api/admin/monthly-data/${year}/${record.month}`,
        { data_type: dataType, locked: !record.locked },
        { headers },
      );
      toast.success(`${record.month_name}: ${!record.locked ? "🔒 Bloqueado" : "🔓 Desbloqueado"}`);
      fetchData();
    } catch (e) {
      toast.error("Error cambiando lock");
    }
  };

  // Bulk paste preview rows (only for selected year)
  const previewRows = parseOscarPaste(bulkText, year).filter((r) => r.year === year);

  const applyBulk = async () => {
    if (previewRows.length === 0) return;
    setBulkApplying(true);
    let ok = 0;
    let fail = 0;
    for (const row of previewRows) {
      try {
        const payload = {
          data_type: dataType,
          revenue: row.revenue,
          locked: false,
        };
        if (dataType === "production") {
          payload.room_nights = row.room_nights;
          payload.total_revenue = row.total_revenue;
        }
        await axios.patch(
          `${API}/api/admin/monthly-data/${row.year}/${row.month}`,
          payload,
          { headers },
        );
        ok += 1;
      } catch (e) {
        fail += 1;
      }
    }
    setBulkApplying(false);
    if (fail === 0) {
      toast.success(`✅ ${ok} meses actualizados desde Oscar`);
      setBulkOpen(false);
      setBulkText("");
      fetchData();
    } else {
      toast.error(`${ok} OK, ${fail} fallaron`);
      fetchData();
    }
  };

  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setBulkText(text);
        toast.success("Pegado desde el portapapeles");
      } else {
        toast.error("Portapapeles vacío");
      }
    } catch (e) {
      toast.error("No se pudo leer el portapapeles");
    }
  };

  const years = [2024, 2025, 2026, 2027];
  const totalRevenue = records.reduce((s, r) => s + (r.revenue || 0), 0);
  const totalNights = records.reduce((s, r) => s + (r.room_nights || 0), 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6 max-w-4xl mx-auto" data-testid="admin-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">
            Administración
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Editar Producción y Facturación directamente desde el móvil.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="bulk-paste-btn"
            variant="default"
            onClick={() => setBulkOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <ClipboardPaste className="w-4 h-4 mr-2" />
            Pegar de Oscar
          </Button>
          <Button
            data-testid="refresh-admin-btn"
            variant="outline"
            onClick={fetchData}
            disabled={loading}
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            <span className="hidden sm:inline">Recargar</span>
          </Button>
        </div>
      </div>

      {/* Year selector */}
      <Card>
        <CardContent className="p-3 sm:p-4 flex items-center gap-2 flex-wrap">
          <Label className="text-sm font-semibold mr-1">Año:</Label>
          {years.map((y) => (
            <Button
              key={y}
              data-testid={`year-btn-${y}`}
              size="sm"
              variant={y === year ? "default" : "outline"}
              onClick={() => setYear(y)}
            >
              {y}
            </Button>
          ))}
        </CardContent>
      </Card>

      {/* Oscar shortcuts */}
      <OscarShortcuts />

      {/* Google sign-in allowlist */}
      <GoogleAccess />

      {/* Type tabs */}
      <Tabs value={dataType} onValueChange={setDataType}>
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger data-testid="tab-production" value="production">Producción (Estancias)</TabsTrigger>
          <TabsTrigger data-testid="tab-billing" value="billing">Facturación</TabsTrigger>
        </TabsList>

        <TabsContent value={dataType} className="mt-4 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <>
              {/* Total */}
              <Card className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                <CardContent className="p-4 flex items-baseline justify-between">
                  <div>
                    <div className="text-xs opacity-90 uppercase tracking-wider">Total {year}</div>
                    <div className="text-2xl sm:text-3xl font-bold font-mono">
                      €{totalRevenue.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div className="text-xs opacity-90 text-right">
                    {dataType === "production" && <>{totalNights.toLocaleString("es-ES")} noches<br /></>}
                    {records.length} meses · {records.filter((r) => r.locked).length} 🔒
                  </div>
                </CardContent>
              </Card>

              {/* Records list */}
              <div className="space-y-2">
                {records.map((r) => (
                  <Card
                    key={r.month}
                    data-testid={`record-${r.month}`}
                    className={`${r.locked ? "border-slate-300 bg-slate-50" : "border-slate-200"} active:scale-[0.99] transition-transform`}
                  >
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-base font-bold text-slate-900">
                              {r.month_name} {year}
                            </span>
                            {r.locked && (
                              <Lock className="w-3.5 h-3.5 text-amber-600" data-testid={`locked-${r.month}`} />
                            )}
                          </div>
                          <div className="font-mono text-lg sm:text-xl font-bold text-slate-900 mt-1">
                            €{(r.revenue || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          {dataType === "production" && (
                            <div className="text-xs text-slate-500 mt-0.5 space-x-2">
                              <span>{(r.room_nights || 0).toLocaleString("es-ES")} noches</span>
                              <span>· ADR €{r.adr || 0}</span>
                              {r.total_revenue && r.total_revenue !== r.revenue && (
                                <span>· Total €{r.total_revenue.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            data-testid={`lock-btn-${r.month}`}
                            size="icon"
                            variant="ghost"
                            onClick={() => toggleLock(r)}
                            title={r.locked ? "Desbloquear" : "Bloquear"}
                          >
                            {r.locked ? (
                              <Unlock className="w-4 h-4 text-slate-600" />
                            ) : (
                              <Lock className="w-4 h-4 text-slate-400" />
                            )}
                          </Button>
                          <Button
                            data-testid={`edit-btn-${r.month}`}
                            size="sm"
                            onClick={() => openEdit(r)}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            <Pencil className="w-3.5 h-3.5 sm:mr-1" />
                            <span className="hidden sm:inline">Editar</span>
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-dialog">
          {editing && (
            <>
              <DialogHeader>
                <DialogTitle>
                  Editar {editing.month_name} {year} · {dataType === "production" ? "Producción" : "Facturación"}
                </DialogTitle>
                <DialogDescription>
                  Los cambios se aplican al instante en la base de datos.
                </DialogDescription>
              </DialogHeader>

              {editing.locked && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-900 flex gap-2">
                  <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    Este mes está <strong>bloqueado</strong>. Desbloquéalo abajo si quieres editarlo.
                  </span>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <Label htmlFor="revenue-input" className="text-sm font-semibold">
                    {dataType === "production" ? "Estancias (€)" : "Facturación (€)"}
                  </Label>
                  <Input
                    id="revenue-input"
                    data-testid="revenue-input"
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    value={editing.revenue}
                    onChange={(e) => setEditing({ ...editing, revenue: parseFloat(e.target.value) || 0 })}
                    disabled={editing.locked}
                    className="font-mono text-lg mt-1"
                  />
                </div>

                {dataType === "production" && (
                  <>
                    <div>
                      <Label htmlFor="total-input" className="text-sm font-semibold">
                        Totales (€) <span className="text-slate-400 font-normal">— Estancias + cancelaciones</span>
                      </Label>
                      <Input
                        id="total-input"
                        data-testid="total-input"
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        value={editing.total_revenue}
                        onChange={(e) => setEditing({ ...editing, total_revenue: parseFloat(e.target.value) || 0 })}
                        disabled={editing.locked}
                        className="font-mono text-lg mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="nights-input" className="text-sm font-semibold">
                        Noches ocupadas
                      </Label>
                      <Input
                        id="nights-input"
                        data-testid="nights-input"
                        type="number"
                        inputMode="numeric"
                        step="1"
                        value={editing.room_nights}
                        onChange={(e) => setEditing({ ...editing, room_nights: parseInt(e.target.value, 10) || 0 })}
                        disabled={editing.locked}
                        className="font-mono text-lg mt-1"
                      />
                    </div>
                  </>
                )}

                <div className="flex items-center justify-between py-2 border-t">
                  <div>
                    <Label htmlFor="lock-toggle" className="text-sm font-semibold cursor-pointer">
                      🔒 Bloqueado
                    </Label>
                    <p className="text-xs text-slate-500">
                      Impide que los sync automáticos lo sobreescriban
                    </p>
                  </div>
                  <Switch
                    id="lock-toggle"
                    data-testid="lock-toggle"
                    checked={editing.locked}
                    onCheckedChange={(v) => setEditing({ ...editing, locked: v })}
                  />
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <Button
                  data-testid="cancel-edit-btn"
                  variant="outline"
                  onClick={() => setEditing(null)}
                  disabled={saving}
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancelar
                </Button>
                <Button
                  data-testid="save-edit-btn"
                  onClick={saveEdit}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-1" />
                  )}
                  Guardar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Paste Dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="bulk-paste-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardPaste className="w-5 h-5 text-indigo-600" />
              Importar desde Oscar — {year}
            </DialogTitle>
            <DialogDescription>
              Pega la tabla de "Datos" desde Oscar (móvil o desktop). Se detectan año, mes,
              noches, totales y estancias automáticamente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex gap-2">
              <Button
                data-testid="paste-clipboard-btn"
                size="sm"
                variant="outline"
                onClick={pasteFromClipboard}
                className="flex-1"
              >
                <ClipboardPaste className="w-4 h-4 mr-2" />
                Pegar del portapapeles
              </Button>
              {bulkText && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setBulkText("")}
                  data-testid="clear-bulk-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>

            <Textarea
              data-testid="bulk-textarea"
              placeholder="Ejemplo:&#10;2026 dic   45    940.60 €    940.60 €&#10;2026 nov   57    1,274.69 €  1,274.69 €&#10;..."
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              rows={6}
              className="font-mono text-xs"
            />

            {/* Preview */}
            {previewRows.length > 0 ? (
              <div className="border border-slate-200 rounded-lg p-2 bg-slate-50 max-h-64 overflow-y-auto">
                <div className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  {previewRows.length} mes(es) detectados — vista previa
                </div>
                <div className="space-y-1">
                  {previewRows.map((r) => (
                    <div
                      key={`${r.year}-${r.month}`}
                      className="grid grid-cols-4 gap-2 text-xs font-mono px-2 py-1 bg-white rounded border border-slate-100"
                      data-testid={`bulk-preview-${r.month}`}
                    >
                      <span className="font-semibold">{MONTH_NAMES_SHORT[r.month - 1]}</span>
                      <span className="text-right">{r.room_nights}n</span>
                      <span className="text-right text-slate-500">Tot €{r.total_revenue.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      <span className="text-right font-bold text-emerald-700">Est €{r.revenue.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : bulkText ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 flex gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>No detecto filas para {year}. Asegúrate de pegar líneas tipo "2026 ene 1485 42,112.53 41,800.70".</span>
              </div>
            ) : null}

            {dataType === "billing" && previewRows.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-xs text-blue-900">
                ℹ️ En modo Facturación solo se aplicará el campo Estancias. Noches y Totales se ignoran.
              </div>
            )}
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <Button
              variant="outline"
              onClick={() => setBulkOpen(false)}
              disabled={bulkApplying}
              data-testid="bulk-cancel-btn"
            >
              <X className="w-4 h-4 mr-1" />
              Cancelar
            </Button>
            <Button
              onClick={applyBulk}
              disabled={previewRows.length === 0 || bulkApplying}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="bulk-apply-btn"
            >
              {bulkApplying ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-1" />
              )}
              Aplicar {previewRows.length > 0 ? `${previewRows.length} mes${previewRows.length > 1 ? "es" : ""}` : ""}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
